import {useState} from 'react';
import {useIntl} from 'react-intl';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppInput} from '@components/Core/inputs/AppInput/AppInput';
import BackButton from '@components/common/BackButton/BackButton';
import {twoFactorApi} from '@api/twoFactor/api';
import useNotifications from '@hooks/useNotifications';
import './twoFactorResetForm.less';

interface TwoFactorResetFormProps {
    email: string;
    onBack: () => void;
    onBackToLogin: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TwoFactorResetForm({email: initialEmail, onBack, onBackToLogin}: TwoFactorResetFormProps) {
    const intl = useIntl();
    const {showSuccessAlert} = useNotifications();
    const [email, setEmail] = useState(initialEmail);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEmailValid = EMAIL_REGEX.test(email);
    const isSubmitDisabled = !isEmailValid || isSubmitting;

    function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
        setEmail(event.target.value);
    }

    async function handleSubmit(event: React.FormEvent) {
        event.preventDefault();

        if (!isEmailValid) return;

        setIsSubmitting(true);

        try {
            await twoFactorApi.requestTwoFactorReset({data: {email}});
        } catch {
            // Always show success to prevent user enumeration
        }

        setIsSubmitting(false);
        showSuccessAlert(intl.formatMessage({
            id: 'page.account.twoFactorAuth.resetRequestSuccess',
            defaultMessage: 'Your account administrator has been notified. They will disable 2FA for your account so you can log in and reconfigure it.'
        }), {position: 'top-center'});
        onBackToLogin();
    }

    return (
        <form className="two-factor-form" onSubmit={handleSubmit}>
            <BackButton onClick={onBack} />
            <p className="two-factor-description">
                {intl.formatMessage({
                    id: 'page.account.twoFactorAuth.resetRequestDescription',
                    defaultMessage: 'Enter the email address associated with your account. Your administrator will be notified to disable 2FA for you.'
                })}
            </p>
            <AppInput
                type="email"
                value={email}
                onChange={handleEmailChange}
                disabled={isSubmitting}
                autoFocus
                fullWidth
                dataWdio="test-reset-2fa-email-input"
            />
            <AppButton
                type="submit"
                disabled={isSubmitDisabled}
                size="small"
                className="two-factor-submit-button"
                data-wdio="test-reset-2fa-submit-button"
                fullWidth
            >
                {isSubmitting
                    ? intl.formatMessage({id: 'esl.generic.loading', defaultMessage: 'Loading...'})
                    : intl.formatMessage({
                        id: 'page.account.twoFactorAuth.resetRequestSubmit',
                        defaultMessage: 'Send Request'
                    })}
            </AppButton>
        </form>
    );
}
